import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { molecularContours, paintMolecularStill } from "./investorMolecularGeometry";
import "./InvestorMolecularVisual.css";

export default function InvestorMolecularVisual({ className = "" }) {
  const surfaceRef = useRef(null);
  const stillRef = useRef(null);
  const canvasRef = useRef(null);
  const controllerRef = useRef(null);
  const pausedRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [mode, setMode] = useState("contours");

  useEffect(() => {
    const element = surfaceRef.current;
    const media = window.matchMedia?.("(min-width: 768px) and (prefers-reduced-motion: no-preference)");
    let disposed = false;
    let visible = !window.IntersectionObserver;
    let loading = false;
    let failed = false;
    let generation = 0;

    function paint() {
      if (typeof CanvasRenderingContext2D === "undefined") return;
      try {
        if (paintMolecularStill(stillRef.current, element) && !controllerRef.current) setMode("still");
      } catch {
        // The vector drawing remains available when a 2D context cannot be made.
      }
    }

    async function sync() {
      if (disposed) return;
      const eligible = Boolean(media?.matches) && !failed;
      if (!eligible) {
        generation += 1;
        loading = false;
        controllerRef.current?.dispose();
        controllerRef.current = null;
        setMode("contours");
        paint();
        return;
      }
      const playing = visible && !document.hidden && !pausedRef.current;
      if (controllerRef.current) {
        controllerRef.current.setRunning(playing);
        return;
      }
      // A paused scene still needs its renderer and Play control after re-entry.
      if (!visible || document.hidden || loading) return;
      loading = true;
      const attempt = ++generation;
      try {
        const { createMolecularRenderer } = await import("./investorMolecularRenderer");
        if (disposed || attempt !== generation || !media.matches || !visible || document.hidden) return;
        const controller = createMolecularRenderer({
          canvas: canvasRef.current,
          element,
          onFailure: () => {
            failed = true;
            sync();
          },
        });
        controllerRef.current = controller;
        setMode("motion");
        controller.setRunning(visible && !document.hidden && !pausedRef.current);
      } catch {
        if (!disposed && attempt === generation) {
          failed = true;
          setMode("contours");
          paint();
        }
      } finally {
        if (attempt === generation) loading = false;
      }
    }

    function resize() {
      paint();
      controllerRef.current?.refresh();
    }

    paint();
    const resizeObserver = window.ResizeObserver ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(element);
    if (!resizeObserver) window.addEventListener("resize", resize);
    const intersectionObserver = window.IntersectionObserver ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    }, { threshold: 0.05 }) : null;
    intersectionObserver?.observe(element);
    const themeObserver = new MutationObserver(resize);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    media?.addEventListener?.("change", sync);
    document.addEventListener("visibilitychange", sync);
    element.addEventListener("molecular-playback-change", sync);
    sync();

    return () => {
      disposed = true;
      generation += 1;
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      themeObserver.disconnect();
      window.removeEventListener("resize", resize);
      media?.removeEventListener?.("change", sync);
      document.removeEventListener("visibilitychange", sync);
      element.removeEventListener("molecular-playback-change", sync);
      controllerRef.current?.dispose();
      controllerRef.current = null;
    };
  }, []);

  function togglePlayback() {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
    surfaceRef.current?.dispatchEvent(new Event("molecular-playback-change"));
  }

  return (
    <figure className={`investor-molecular ${className}`} data-rendering={mode}>
      <div className="investor-molecular-surface" ref={surfaceRef} role="img" aria-label="A folded blue molecular ribbon. Conceptual scientific illustration, not a drug structure.">
        <svg className="investor-molecular-contours" viewBox="0 0 400 400" fill="none" aria-hidden="true" focusable="false">
          {molecularContours.map(({ depth, path }, index) => <path key={index} d={path} opacity={0.35 + (depth + 1.5) / 5} />)}
        </svg>
        <canvas className="investor-molecular-still" ref={stillRef} aria-hidden="true" />
        <canvas className="investor-molecular-canvas" ref={canvasRef} aria-hidden="true" />
      </div>
      {mode === "motion" && <button className="investor-molecular-toggle" type="button" onClick={togglePlayback} aria-label={paused ? "Play molecular animation" : "Pause molecular animation"}>{paused ? <Play size={13} aria-hidden="true" /> : <Pause size={13} aria-hidden="true" />}</button>}
    </figure>
  );
}
